import React, { useEffect, useRef, useState } from "react";

import API from "../../api";

const ExamSearchInput = ({ value, onChange, onPick, enabled = true, autoFocus, onEnter, onEscape, onBlur, placeholder }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pickedRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!enabled || (value || "").trim().length < 2) {
      setSuggestions([]);
      setHighlighted(-1);
      return;
    }
    if (pickedRef.current === value) return;

    const timeout = setTimeout(async () => {
      try {
        const response = await API.get(`/exam?q=${encodeURIComponent(value.trim())}`);
        if (response?.ok) {
          setSuggestions(response.data || []);
          setHighlighted(-1);
          setOpen(true);
        }
      } catch (error) {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [value, enabled]);

  useEffect(() => {
    if (highlighted >= 0) listRef.current?.children[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const pick = (exam) => {
    pickedRef.current = exam.value;
    onPick(exam.value);
    setOpen(false);
    setHighlighted(-1);
  };

  const tabTarget = highlighted >= 0 ? highlighted : 0;

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setHighlighted((previous) => (previous + 1) % suggestions.length);
      return;
    }
    if (event.key === "ArrowUp" && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setHighlighted((previous) => (previous <= 0 ? suggestions.length - 1 : previous - 1));
      return;
    }
    if (event.key === "Tab" && open && suggestions.length > 0) {
      event.preventDefault();
      pick(suggestions[tabTarget]);
      return;
    }
    if (event.key === "Enter") {
      if (open && highlighted >= 0 && suggestions[highlighted]) return pick(suggestions[highlighted]);
      onEnter?.();
      return;
    }
    if (event.key === "Escape") {
      if (open) {
        setOpen(false);
        setHighlighted(-1);
        return;
      }
      onEscape?.();
    }
  };

  return (
    <div className="relative min-w-0 flex-1">
      <input
        ref={inputRef}
        className="w-full rounded-md border border-primary-500 px-1.5 py-0.5 text-sm outline-none"
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          pickedRef.current = null;
          onChange(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => {
          setOpen(false);
          onBlur?.();
        }, 150)}
      />
      {open && suggestions.length > 0 && (
        <div ref={listRef} className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {suggestions.map((exam, index) => (
            <button
              key={index}
              type="button"
              className={`flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-sm ${index === highlighted ? "bg-primary-50" : "hover:bg-gray-50"}`}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(exam);
              }}
            >
              <span className="min-w-0 truncate text-gray-800">{exam.value}</span>
              <span className="flex shrink-0 items-center gap-1.5">
                {exam.codes?.length > 0 && <span className="text-[10px] tabular-nums text-gray-400">{exam.codes.join(", ")}</span>}
                {index === tabTarget && <span className="rounded border border-gray-200 px-1 text-[10px] text-gray-400">⇥</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExamSearchInput;
